import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
import { AudioPlayer } from './components/AudioPlayer';
import { PDFReader } from './components/PDFReader';
import { AudiobookData, MusicService, Bookmark, Playlist, AudioLanguage } from './types';
import { Book, Headphones, Settings, Bookmark as BookmarkIcon, ChevronDown, Send, Music, BookOpen, Instagram, Mail, ArrowLeft } from 'lucide-react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { trackAudiobookEvent } from './utils/analytics';

// Lazy load components that aren't immediately visible
const MusicPlayer = lazy(() => import('./components/MusicPlayer').then(module => ({ default: module.MusicPlayer })));
const ChapterList = lazy(() => import('./components/ChapterList').then(module => ({ default: module.ChapterList })));
const BookmarkList = lazy(() => import('./components/BookmarkList').then(module => ({ default: module.BookmarkList })));
const PlaylistManager = lazy(() => import('./components/PlaylistManager').then(module => ({ default: module.PlaylistManager })));

// Lazy load 3D Landing component
const Landing3D = lazy(() => import('./components/3d/Landing3D'));
const PDFViewerComponent = lazy(() => import('./components/PDFViewer'));

// View types for navigation
type AppView = 'landing' | 'audiobook' | 'pdf';

// Optimized loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    minHeight: '100px'
  }}>
    <div style={{
      width: '20px',
      height: '20px',
      border: '2px solid #ff6b35',
      borderTop: '2px solid transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
  </div>
);


const mockAudiobook: AudiobookData = {
  title: "11000",
  author: "Dante Zetta",
  availableLanguages: ['es', 'en', 'fr', 'it'],
  chapters: [
    // CAPÍTULOS CON AUDIO DISPONIBLE
    { 
      id: '1', 
      title: 'INTRO', 
      audioUrl: '/audio/optimized/11K_1_INTRO_optimized.mp3', 
      duration: 178,
      audioUrls: {
        'es': '/audio/es/11K_1_INTRO.mp3',
        'en': '/audio/en/11K_1._INTRO.mp3',
        'fr': '/audio/fr/11K_1._INTRO.mp3',
        'it': '/audio/it/11K_1._INTRO.mp3'
      }
    },
    { 
      id: '2', 
      title: 'EL ORIGEN DEL MAL', 
      audioUrl: '/audio/optimized/11K_2_EL_ORIGEN_DEL_MAL_optimized.mp3', 
      duration: 374,
      audioUrls: {
        'es': '/audio/es/11K_2_EL_ORIGEN_DEL_MAL.mp3',
        'en': '/audio/en/11K_2._THE_ORIGIN_OF_EVIL.mp3',
        'fr': '/audio/fr/11K_2._L_ORIGINE_DU_MAL.mp3',
        'it': '/audio/it/11K_2._L_ORIGINE_DEL_MALE.mp3'
      }
    },
    { 
      id: '3', 
      title: 'BLANCA Y RADIACTIVA', 
      audioUrl: '/audio/optimized/11K_3_BLANCA_Y_RADIACTIVA_optimized.mp3', 
      duration: 410,
      audioUrls: {
        'es': '/audio/es/11K_3_BLANCA_Y_RADIACTIVA.mp3',
        'en': '/audio/en/11K_3._WHITE_AND_RADIOACTIVE.mp3',
        'fr': '/audio/fr/11K_3._BLANCHE_ET_RADIOACTIVE.mp3',
        'it': '/audio/it/11K_3._BIANCA_E_RADIOATTIVA.mp3'
      }
    },
    { 
      id: '4', 
      title: 'CHAU PETISA', 
      audioUrl: '/audio/optimized/11K_4_CHAU_PETISA_optimized.mp3', 
      duration: 121,
      audioUrls: {
        'es': '/audio/es/11K_4_CHAU_PETISA.mp3',
        'en': '/audio/en/11K_4._GOODBYE_SHORTY.mp3',
        'fr': '/audio/fr/11K_4._ADIEU_PETITE.mp3',
        'it': '/audio/it/11K_4._CIAO_PICCOLA.mp3'
      }
    },
    { 
      id: '5', 
      title: 'MARITO', 
      audioUrl: '/audio/optimized/11K_5_MARITO_optimized.mp3', 
      duration: 1197,
      audioUrls: {
        'es': '/audio/es/11K_5_MARITO.mp3',
        'en': '/audio/en/11K_5._MARITO.mp3',
        'fr': '/audio/fr/11K_5._MARITO.mp3',
        'it': '/audio/it/11K_5._MARITO.mp3'
      }
    },
    { 
      id: '6', 
      title: 'ARBÚSBOIL', 
      audioUrl: '/audio/optimized/11K_6_ARBUSBOIL_optimized.mp3', 
      duration: 853,
      audioUrls: {
        'es': '/audio/11K_6_ARBUSBOIL.mp3',
        'en': '/audio/en/11K_6_ARBUSBOIL.mp3',
        'fr': '/audio/fr/11K_6_ARBUSBOIL.mp3',
        'it': '/audio/it/11K_6_ARBUSBOIL.mp3'
      }
    },
    { 
      id: '7', 
      title: 'ALGO CAYÓ DEL CIELO', 
      audioUrl: '/audio/optimized/11K_7_ALGO_CAYO_DEL_CIELO_optimized.mp3', 
      duration: 1495,
      audioUrls: {
        'es': '/audio/11K_7_ALGO_CAYO_DEL_CIELO.mp3',
        'en': '/audio/en/11K_7_SOMETHING_FELL_FROM_THE_SKY.mp3',
        'fr': '/audio/fr/11K_7_QUELQUE_CHOSE_EST_TOMBE_DU_CIEL.mp3',
        'it': '/audio/it/11K_7_QUALCOSA_E_CADUTO_DAL_CIELO.mp3'
      }
    },
    {
      id: '8',
      title: '¡VIVA LA VIDA!',
      audioUrl: '/audio/optimized/11K_8_VIVA_LA_VIDA_optimized.mp3',
      duration: 639,
      audioUrls: {
        'es': '/audio/es/11K_8_VIVA_LA_VIDA.mp3',
        'en': '/audio/en/11K_8._VIVA_LA_VIDA.mp3',
        'fr': '/audio/fr/11K_8._VIVA_LA_VIDA.mp3',
        'it': '/audio/it/11K_8._VIVA_LA_VIDA.mp3'
      }
    },
    // CAPÍTULOS PRÓXIMAMENTE
    {
      id: '9',
      title: 'EL GRAN MOJÓN (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '10',
      title: 'POLAROIDS SOBRE UN ATAÚD (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '11',
      title: 'BANG, BANG (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '12',
      title: 'EL ZOMBI (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '13',
      title: 'MUTTER (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '14',
      title: 'VOLARÉ... OH, OH (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '15',
      title: 'CHRISTOPHORUS COLUMBUS (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '16',
      title: 'MALDITA MILÁN (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '17',
      title: 'WILLI & JULIANO (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '18',
      title: 'BARCELONA (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '19',
      title: 'TRAUMA DE GUERRA (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '20',
      title: 'JUSTICIA POÉTICA (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '21',
      title: 'COSAS QUE PASAN (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '22',
      title: 'PASARON COSAS (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '23',
      title: 'LA PROMESA (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '24',
      title: 'PUTO EL QUE ESCRIBE (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '25',
      title: 'LA POSIBILIDAD DE UNA ISLA (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '26',
      title: 'HIJA DE LORD BYRON (proximamente...)',
      audioUrl: '',
      duration: 0
    },
    {
      id: '27',
      title: 'FINAL (proximamente...)',
      audioUrl: '',
      duration: 0
    }
  ]
};

// Background music mapping: Chapter ID → Local MP3 file (works with screen off!)
const CHAPTER_BACKGROUND_MUSIC: { [key: string]: string } = {
  '1': '/audio/background/vBxONOXKzWE.mp3',
  '2': '/audio/background/pbihIAPsKZ0.mp3',
  '3': '/audio/background/CDHTso6wIE8.mp3',
  '4': '/audio/background/mUwsrxFisTs.mp3',
  '5': '/audio/background/hGQu4_fan8Q.mp3',
  '6': '/audio/background/eNZjvoT16_g.mp3',
  '7': '/audio/background/rKD_P2aqCi0.mp3',
  '8': '/audio/background/FjCPbR5sg-Y.mp3',
};

// Default background music volumes per chapter (0.0 to 1.0)
const CHAPTER_DEFAULT_VOLUMES: { [key: string]: number } = {
  '1': 0.22,
  '2': 0.22,
  '3': 0.22,
  '4': 0.12, // CHAU PETISA - lower volume
  '5': 0.22,
  '6': 0.22,
  '7': 0.22,
  '8': 0.22,
};

function AppContent() {
  const { t, setLanguage } = useLanguage();
  const [audiobook] = useState<AudiobookData>(mockAudiobook);

  // View navigation state - starts at 3D landing
  const [currentView, setCurrentView] = useState<AppView>('landing');

  // Handle book click from 3D landing
  const handleBookClick = (bookIndex: number) => {
    if (bookIndex === 0) {
      // First book (11.000) - go to audiobook player
      setCurrentView('audiobook');
    } else if (bookIndex === 1) {
      // Second book (MANIFESTATIO) - go to PDF viewer
      setCurrentView('pdf');
    }
  };

  // Pause all music when leaving audiobook view
  useEffect(() => {
    if (currentView !== 'audiobook') {
      // Pause chapter background music
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.pause();
      }
      // Pause YouTube player
      if (youtubePlayerRef.current?.pauseVideo) {
        try {
          youtubePlayerRef.current.pauseVideo();
        } catch (e) {
          console.log('Could not pause YouTube player');
        }
      }
      // Pause persistent audio player (local playlists)
      const persistentAudio = document.getElementById('persistent-audio-player') as HTMLAudioElement;
      if (persistentAudio) {
        persistentAudio.pause();
      }
      // Reset playback states
      setIsBackgroundMusicPlaying(false);
      setIsPlaylistPlaying(false);
    }
  }, [currentView]);

  // Detect if we're on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const musicAudioRef = useRef<HTMLAudioElement>(null);
  const backgroundMusicRef = useRef<HTMLAudioElement>(null);

  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set());
  const [isPDFOpen, setIsPDFOpen] = useState(false);
  const [musicService, setMusicService] = useState<MusicService>(() => {
    const saved = localStorage.getItem('audiobookMusicService');
    return (saved as MusicService) || 'chapter';
  });
  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('audiobookMusicVolume');
    return saved ? parseFloat(saved) : 0.22; // 25% menos que el volumen estándar (0.3)
  });
  const [spotifyUri, setSpotifyUri] = useState(() => {
    const saved = localStorage.getItem('audiobookSpotifyUri');
    return saved || '';
  });
  const [youtubeUrl, setYoutubeUrl] = useState(() => {
    const saved = localStorage.getItem('audiobookYoutubeUrl');
    console.log('🔄 Loading youtubeUrl from localStorage:', saved);
    return saved || '';
  });
  const [playbackRate, setPlaybackRate] = useState(1.0); // 1.0 = velocidad normal (rango: 0.9 - 1.2)
  const youtubePlayerRef = useRef<any>(null);
  const [isYoutubeReady, setIsYoutubeReady] = useState(false);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(() => {
    // Cargar marcadores inmediatamente desde localStorage
    const saved = localStorage.getItem('audiobookBookmarks');
    return saved ? JSON.parse(saved) : [];
  });
  const [bookmarkToLoad, setBookmarkToLoad] = useState<{ chapterId: string; timestamp: number } | null>(null);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [playlists, setPlaylists] = useState<Playlist[]>(() => {
    const saved = localStorage.getItem('audiobookPlaylists');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentPlaylist, setCurrentPlaylist] = useState<Playlist | null>(() => {
    const saved = localStorage.getItem('audiobookCurrentPlaylist');
    return saved ? JSON.parse(saved) : null;
  });
  const [currentPlaylistTrack, setCurrentPlaylistTrack] = useState(() => {
    const saved = localStorage.getItem('audiobookCurrentTrack');
    return saved ? parseInt(saved) : 0;
  });
  const [isPlaylistPlaying, setIsPlaylistPlaying] = useState(() => {
    const saved = localStorage.getItem('audiobookPlaylistPlaying');
    return saved ? JSON.parse(saved) : false;
  });
  const [isShuffled, setIsShuffled] = useState(() => {
    const saved = localStorage.getItem('audiobookPlaylistShuffled');
    return saved ? JSON.parse(saved) : false;
  });
  const [isRepeating, setIsRepeating] = useState(() => {
    const saved = localStorage.getItem('audiobookPlaylistRepeating');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Collapsible sections state - all collapsed by default
  const [collapsedSections, setCollapsedSections] = useState({
    chapters: true,
    music: true,
    bookmarks: true,
    settings: true
  });
  
  // Audio language state
  const [audioLanguage, setAudioLanguage] = useState<AudioLanguage>(() => {
    const saved = localStorage.getItem('audiobookAudioLanguage');
    return (saved as AudioLanguage) || 'es';
  });

  // Track if user has manually loaded a YouTube URL (to prevent auto-override)
  const [hasManualYoutubeUrl, setHasManualYoutubeUrl] = useState(false); // Prevent auto-populate override

  // Save audio language to localStorage
  useEffect(() => {
    localStorage.setItem('audiobookAudioLanguage', audioLanguage);
  }, [audioLanguage]);

  // Auto-populate YouTube Music URLs for chapters 1-8 (for UI display)
  // AND auto-load local MP3s for background playback (works with screen off!)
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    const currentChapter = audiobook.chapters[currentChapterIndex];

    if (currentChapter && (currentChapter.id === '1' || currentChapter.id === '2' || currentChapter.id === '3' || currentChapter.id === '4' || currentChapter.id === '5' || currentChapter.id === '6' || currentChapter.id === '7' || currentChapter.id === '8')) {
      console.log(`🚀 Auto-populating YouTube URL (for UI) and MP3 (for background) for chapter ${currentChapter.id}`);

      // Only auto-populate if user hasn't manually loaded a YouTube URL
      if (!hasManualYoutubeUrl) {
        setTimeout(() => {
          // Do NOT force service; only auto-populate URL when service is youtube
          if (musicService === 'youtube') {
            // Set YouTube URL for UI display
            if (currentChapter.id === '1') {
              setYoutubeUrl('https://music.youtube.com/watch?v=vBxONOXKzWE');
            } else if (currentChapter.id === '2') {
              setYoutubeUrl('https://music.youtube.com/watch?v=pbihIAPsKZ0');
            } else if (currentChapter.id === '3') {
              setYoutubeUrl('https://music.youtube.com/watch?v=CDHTso6wIE8');
            } else if (currentChapter.id === '4') {
              setYoutubeUrl('https://music.youtube.com/watch?v=mUwsrxFisTs');
            } else if (currentChapter.id === '5') {
              setYoutubeUrl('https://music.youtube.com/watch?v=hGQu4_fan8Q');
            } else if (currentChapter.id === '6') {
              setYoutubeUrl('https://music.youtube.com/watch?v=eNZjvoT16_g');
            } else if (currentChapter.id === '7') {
              setYoutubeUrl('https://music.youtube.com/watch?v=rKD_P2aqCi0');
            } else if (currentChapter.id === '8') {
              setYoutubeUrl('https://music.youtube.com/watch?v=FjCPbR5sg-Y');
            }
            console.log(`✅ YouTube URL set for UI (service is youtube)`);
          }
        }, 50);
      } else {
        console.log(`ℹ️ Skipping auto-populate - user has manual YouTube URL`);
      }

      // Load/clear local MP3 for background playback based on selected music service
      const backgroundMusicUrl = CHAPTER_BACKGROUND_MUSIC[currentChapter.id];
      if (backgroundMusicRef.current) {
        if (musicService === 'chapter' && backgroundMusicUrl) {
          console.log(`🎵 Chapter service active; loading local MP3:`, backgroundMusicUrl);
          backgroundMusicRef.current.src = backgroundMusicUrl;
          // Do NOT auto-start here. Background should start when user presses Play.
        } else {
          // Clear source when not using chapter music service or when there is no chapter track
          backgroundMusicRef.current.pause();
          backgroundMusicRef.current.removeAttribute('src');
        }
      }
    } else if (backgroundMusicRef.current) {
      // Clear background music for chapters without it
      backgroundMusicRef.current.pause();
      backgroundMusicRef.current.removeAttribute('src');
    }
  }, [currentView, currentChapterIndex, audiobook.chapters, musicService]); // service-aware, view-aware


  useEffect(() => {
    const saved = localStorage.getItem('audiobookProgress');
    if (saved) {
      const data = JSON.parse(saved);
      setCurrentChapterIndex(data.currentChapterIndex || 0);
      setCompletedChapters(new Set(data.completedChapters || []));
    }

    // Restaurar marcador pendiente después del refresh
    const pendingBookmark = localStorage.getItem('pendingBookmark');
    if (pendingBookmark) {
      try {
        const bookmark = JSON.parse(pendingBookmark);
        console.log('🔖 Restaurando marcador pendiente:', bookmark);
        setBookmarkToLoad({ chapterId: bookmark.chapterId, timestamp: bookmark.timestamp });
        // Limpiar marcador pendiente
        localStorage.removeItem('pendingBookmark');
      } catch (error) {
        console.error('Error restaurando marcador pendiente:', error);
        localStorage.removeItem('pendingBookmark');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('audiobookProgress', JSON.stringify({
      currentChapterIndex,
      completedChapters: Array.from(completedChapters),
    }));
  }, [currentChapterIndex, completedChapters]);

  useEffect(() => {
    // Solo guardar si hay marcadores
    if (bookmarks.length > 0) {
      localStorage.setItem('audiobookBookmarks', JSON.stringify(bookmarks));
      console.log('Marcadores guardados:', bookmarks.length);
    } else {
      // Si no hay marcadores, verificar si realmente queremos borrar
      const existingBookmarks = localStorage.getItem('audiobookBookmarks');
      if (existingBookmarks && JSON.parse(existingBookmarks).length > 0) {
        // No borrar si había marcadores antes
        console.log('Preservando marcadores existentes');
      } else {
        localStorage.setItem('audiobookBookmarks', JSON.stringify([]));
      }
    }
  }, [bookmarks]);

  useEffect(() => {
    localStorage.setItem('audiobookPlaylists', JSON.stringify(playlists));
  }, [playlists]);

  // Save current playlist state to localStorage
  useEffect(() => {
    if (currentPlaylist) {
      localStorage.setItem('audiobookCurrentPlaylist', JSON.stringify(currentPlaylist));
    } else {
      localStorage.removeItem('audiobookCurrentPlaylist');
    }
  }, [currentPlaylist]);

  useEffect(() => {
    console.log('📊 Current playlist track changed to:', currentPlaylistTrack);
    localStorage.setItem('audiobookCurrentTrack', currentPlaylistTrack.toString());
  }, [currentPlaylistTrack]);

  useEffect(() => {
    localStorage.setItem('audiobookPlaylistPlaying', JSON.stringify(isPlaylistPlaying));
  }, [isPlaylistPlaying]);

  useEffect(() => {
    localStorage.setItem('audiobookPlaylistShuffled', JSON.stringify(isShuffled));
  }, [isShuffled]);

  useEffect(() => {
    localStorage.setItem('audiobookPlaylistRepeating', JSON.stringify(isRepeating));
  }, [isRepeating]);

  // Save music service and volume
  useEffect(() => {
    console.log('Music service changed to:', musicService);
    localStorage.setItem('audiobookMusicService', musicService);
  }, [musicService]);

  useEffect(() => {
    localStorage.setItem('audiobookMusicVolume', musicVolume.toString());
  }, [musicVolume]);

  useEffect(() => {
    localStorage.setItem('audiobookSpotifyUri', spotifyUri);
  }, [spotifyUri]);

  useEffect(() => {
    console.log('💾 Saving youtubeUrl to localStorage:', youtubeUrl);
    localStorage.setItem('audiobookYoutubeUrl', youtubeUrl);
  }, [youtubeUrl]);


  // Restore/fix service only on initial app load
  useEffect(() => {
    if (currentPlaylist && musicService === 'playlist') {
      console.log('Playlist service restored on app load');
    }
    // If service is youtube but there's no manual URL and chapter has default bg, prefer 'chapter'
    try {
      const currentChapter = audiobook.chapters[currentChapterIndex];
      const hasDefaultBg = CHAPTER_BACKGROUND_MUSIC[currentChapter?.id || ''];
      if (musicService === 'youtube') {
        const savedYoutube = localStorage.getItem('audiobookYoutubeUrl') || youtubeUrl;
        if (!savedYoutube && hasDefaultBg) {
          console.log('🔁 Switching default service to chapter (no youtube URL, chapter has default bg)');
          setMusicService('chapter');
        }
      } else if (musicService === 'none' && hasDefaultBg) {
        console.log('🔁 Switching service none → chapter (has default bg)');
        setMusicService('chapter');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // Removed auto-sync background music with chapter to ensure independent controls

  // Auto-adjust background music volume based on chapter default
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    const currentChapter = audiobook.chapters[currentChapterIndex];
    if (currentChapter && CHAPTER_DEFAULT_VOLUMES[currentChapter.id]) {
      const defaultVolume = CHAPTER_DEFAULT_VOLUMES[currentChapter.id];
      setMusicVolume(defaultVolume);
      console.log(`🔊 Auto-adjusted background volume for chapter "${currentChapter.title}" to ${Math.round(defaultVolume * 100)}%`);
    }
  }, [currentView, currentChapterIndex, audiobook.chapters]);

  // Control background music volume
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (backgroundMusicRef.current) {
      try {
        const { setGainForElement } = require('./utils/audioGraph');
        setGainForElement(backgroundMusicRef.current, musicVolume);
      } catch {
        backgroundMusicRef.current.volume = musicVolume;
      }
      console.log('🔊 Background music volume set to:', musicVolume);
    }
  }, [currentView, musicVolume]);

  // Track if we should auto-play after chapter change
  const [shouldAutoPlayNextChapter, setShouldAutoPlayNextChapter] = useState(false);

  // Track background music playback state
  const [isBackgroundMusicPlaying, setIsBackgroundMusicPlaying] = useState(false);

  const handleNextChapter = () => {
    if (currentChapterIndex < audiobook.chapters.length - 1) {
      const currentChapter = audiobook.chapters[currentChapterIndex];
      const nextChapter = audiobook.chapters[currentChapterIndex + 1];
      setCompletedChapters(prev => new Set(Array.from(prev).concat(currentChapter.id)));

      // Track navigation
      trackAudiobookEvent.chapterNavigation('next_button', currentChapter.title, nextChapter.title);

      // Signal that we want auto-play on the next chapter
      setShouldAutoPlayNextChapter(true);

      setCurrentChapterIndex(currentChapterIndex + 1);

      console.log('➡️ Moving to next chapter with auto-play - NO PAGE REFRESH (works on mobile too)');
      // NO MORE PAGE REFRESH - individual audio elements handle chapter changes
      // This works on both desktop and mobile devices
    }
  };

  const handlePreviousChapter = () => {
    if (currentChapterIndex > 0) {
      const currentChapter = audiobook.chapters[currentChapterIndex];
      const previousChapter = audiobook.chapters[currentChapterIndex - 1];

      // Track navigation
      trackAudiobookEvent.chapterNavigation('previous_button', currentChapter.title, previousChapter.title);

      // Signal that we want auto-play on the previous chapter
      setShouldAutoPlayNextChapter(true);

      setCurrentChapterIndex(currentChapterIndex - 1);

      console.log('⬅️ Moving to previous chapter with auto-play - NO PAGE REFRESH (works on mobile too)');
      // NO MORE PAGE REFRESH - individual audio elements handle chapter changes
      // This works on both desktop and mobile devices
    }
  };

  const handleChapterSelect = (index: number) => {
    console.log('🎯 handleChapterSelect called with index:', index, 'current:', currentChapterIndex);

    // Track chapter navigation
    const fromChapter = audiobook.chapters[currentChapterIndex];
    const toChapter = audiobook.chapters[index];
    if (fromChapter && toChapter && index !== currentChapterIndex) {
      trackAudiobookEvent.chapterNavigation('chapter_list', fromChapter.title, toChapter.title);
    }

    setCurrentChapterIndex(index);

    // Track chapter selection
    const chapter = audiobook.chapters[index];
    if (chapter) {
      trackAudiobookEvent.play(chapter.title, audioLanguage);

      console.log('🔍 Chapter selected:', {
        index,
        id: chapter.id,
        title: chapter.title
      });

      // Auto-populate YouTube Music for chapters 1-8 (URL for UI, local MP3 loaded automatically)
      if (chapter.id === '1') {
        console.log('🎵 Auto-populating background music for chapter 1');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=vBxONOXKzWE');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '2') {
        console.log('🎵 Auto-populating background music for chapter 2 (El origen del mal)');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=pbihIAPsKZ0');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '3') {
        console.log('🎵 Auto-populating background music for chapter 3');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=CDHTso6wIE8');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '4') {
        console.log('🎵 Auto-populating background music for chapter 4');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=mUwsrxFisTs');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '5') {
        console.log('🎵 Auto-populating background music for chapter 5');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=hGQu4_fan8Q');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '6') {
        console.log('🎵 Auto-populating background music for chapter 6');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=eNZjvoT16_g');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '7') {
        console.log('🎵 Auto-populating background music for chapter 7');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=rKD_P2aqCi0');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else if (chapter.id === '8') {
        console.log('🎵 Auto-populating background music for chapter 8');
        if (musicService === 'youtube' && !hasManualYoutubeUrl) {
          setYoutubeUrl('https://music.youtube.com/watch?v=FjCPbR5sg-Y');
        }
        console.log('✅ YouTube Music URL set - user volume preserved');
      } else {
        console.log(`ℹ️ Chapter ${chapter.id} has no auto-music loading`);
      }
    }

    console.log('📖 Chapter selected - NO PAGE REFRESH');
    // NO MORE PAGE REFRESH - individual audio elements handle chapter changes
  };

  const handleSaveBookmark = () => {
    const currentChapter = audiobook.chapters[currentChapterIndex];
    if (!currentChapter) return;

    const newBookmark: Bookmark = {
      id: Date.now().toString(),
      chapterId: currentChapter.id,
      chapterTitle: currentChapter.title,
      timestamp: currentPlayTime,
      createdAt: new Date().toISOString(),
    };

    const updatedBookmarks = [newBookmark, ...bookmarks];
    setBookmarks(updatedBookmarks);
    // Guardar inmediatamente en localStorage
    localStorage.setItem('audiobookBookmarks', JSON.stringify(updatedBookmarks));
    alert(t.bookmarkSaved);
    
    // Track bookmark creation
    trackAudiobookEvent.bookmark(currentChapter.title, currentPlayTime);
  };

  const handleLoadBookmark = (bookmark: Bookmark) => {
    const chapterIndex = audiobook.chapters.findIndex(ch => ch.id === bookmark.chapterId);
    if (chapterIndex !== -1) {
      console.log('🔖 Cargando marcador:', bookmark);

      // Track bookmark usage
      const chapter = audiobook.chapters[chapterIndex];
      if (chapter) {
        trackAudiobookEvent.bookmarkUsed(chapter.title, bookmark.timestamp);

        // Track navigation if changing chapters
        if (chapterIndex !== currentChapterIndex) {
          const fromChapter = audiobook.chapters[currentChapterIndex];
          trackAudiobookEvent.chapterNavigation('bookmark', fromChapter.title, chapter.title);
        }
      }

      // Si ya estamos en el capítulo correcto, cargar directamente
      if (chapterIndex === currentChapterIndex) {
        console.log('🔖 Mismo capítulo - carga directa');
        setBookmarkToLoad({ chapterId: bookmark.chapterId, timestamp: bookmark.timestamp });
      } else {
        console.log('🔖 Capítulo diferente - cambiar sin refrescar');
        setCurrentChapterIndex(chapterIndex);
        // Set bookmark to load after chapter change
        setTimeout(() => {
          setBookmarkToLoad({ chapterId: bookmark.chapterId, timestamp: bookmark.timestamp });
        }, 500); // Wait for new audio element to initialize
      }
    }
  };

  const handleDeleteBookmark = (id: string) => {
    // Track bookmark deletion
    const bookmark = bookmarks.find(b => b.id === id);
    if (bookmark) {
      const chapter = audiobook.chapters.find(ch => ch.id === bookmark.chapterId);
      if (chapter) {
        trackAudiobookEvent.bookmarkDeleted(chapter.title, bookmark.timestamp);
      }
    }

    const updatedBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(updatedBookmarks);
    // Guardar inmediatamente en localStorage
    localStorage.setItem('audiobookBookmarks', JSON.stringify(updatedBookmarks));
  };

  const toggleSection = (section: 'chapters' | 'music' | 'bookmarks' | 'settings') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleMusicServiceChange = (newService: MusicService) => {
    // Track background music service change
    const enabled: boolean = newService !== 'chapter' || Boolean(backgroundMusicRef.current && !backgroundMusicRef.current.paused);
    const currentChapter = audiobook.chapters[currentChapterIndex];
    if (currentChapter) {
      trackAudiobookEvent.backgroundMusicToggle(enabled, newService, currentChapter.title);
    }

    setMusicService(newService);
  };

  const handleShareWhatsApp = () => {
    // Use clean URL without refresh parameter
    const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const message = encodeURIComponent(`🎧 Escuchá "${audiobook.title}", con música de fondo.\n\n${cleanUrl}`);
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePlayMusicNote = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (musicAudioRef.current) {
      if (musicAudioRef.current.paused) {
        console.log('🎵 Playing footer music note - will coexist with audiobook...');
        musicAudioRef.current.play().catch((error) => {
          console.error('❌ Error playing music:', error);
        });
      } else {
        console.log('⏸️ Pausing music note...');
        musicAudioRef.current.pause();
      }
    } else {
      console.error('❌ Music audio ref not available');
    }
  };


  // Get chapter with correct language audio URL
  const getChapterWithLanguage = (chapterIndex: number) => {
    const chapter = audiobook.chapters[chapterIndex];
    if (!chapter) return null;
    
    // For now, always use the original audioUrl since multi-language files don't exist yet
    // Later this can be: const languageAudioUrl = chapter.audioUrls?.[audioLanguage] || chapter.audioUrl;
    const languageAudioUrl = chapter.audioUrl;
    
    return {
      ...chapter,
      audioUrl: languageAudioUrl
    };
  };

  // Get next chapter for smart preloading
  const getNextChapterWithLanguage = () => {
    if (currentChapterIndex < audiobook.chapters.length - 1) {
      return getChapterWithLanguage(currentChapterIndex + 1);
    }
    return null;
  };

  const handleNextTrack = () => {
    console.log('=== handleNextTrack START ===');
    console.log('currentPlaylist:', currentPlaylist);
    console.log('currentPlaylistTrack:', currentPlaylistTrack);
    console.log('isShuffled:', isShuffled);
    console.log('isRepeating:', isRepeating);
    
    if (!currentPlaylist) {
      console.log('❌ No current playlist, returning');
      return;
    }
    
    console.log('✅ Current playlist exists:', currentPlaylist.name);
    console.log('🎵 Total tracks:', currentPlaylist.tracks.length);
    console.log('🎬 Is YouTube playlist?', currentPlaylist.type === 'youtube');
    
    if (isShuffled) {
      const randomIndex = Math.floor(Math.random() * currentPlaylist.tracks.length);
      console.log('🔀 Shuffle mode - going to random track:', randomIndex);
      setCurrentPlaylistTrack(randomIndex);
    } else {
      const nextIndex = (currentPlaylistTrack + 1) % currentPlaylist.tracks.length;
      console.log('➡️  Normal mode - going to next track:', nextIndex, 'from current:', currentPlaylistTrack);
      setCurrentPlaylistTrack(nextIndex);
    }
    console.log('=== handleNextTrack END ===');
  };

  const handlePreviousTrack = () => {
    console.log('=== handlePreviousTrack START ===');
    console.log('currentPlaylist:', currentPlaylist);
    console.log('currentPlaylistTrack:', currentPlaylistTrack);
    
    if (!currentPlaylist) {
      console.log('❌ No current playlist, returning');
      return;
    }
    
    console.log('✅ Current playlist exists:', currentPlaylist.name);
    console.log('🎵 Total tracks:', currentPlaylist.tracks.length);
    
    const prevIndex = currentPlaylistTrack > 0 
      ? currentPlaylistTrack - 1 
      : currentPlaylist.tracks.length - 1;
    console.log('⬅️  Going to previous track:', prevIndex, 'from current:', currentPlaylistTrack);
    setCurrentPlaylistTrack(prevIndex);
    console.log('=== handlePreviousTrack END ===');
  };

  // YouTube API initialization - load when needed for any YouTube functionality
  useEffect(() => {
    // Only load YouTube API when in audiobook view
    if (currentView !== 'audiobook') return;

    const needsYoutube = (musicService === 'youtube' && youtubeUrl) ||
                        (musicService === 'playlist' && currentPlaylist?.type === 'youtube');

    if (needsYoutube && !window.YT && !isYoutubeReady) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      tag.defer = true;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      (window as any).onYouTubeIframeAPIReady = () => {
        setIsYoutubeReady(true);
      };
    } else if (window.YT) {
      setIsYoutubeReady(true);
    }
  }, [currentView, musicService, youtubeUrl, currentPlaylist?.type, isYoutubeReady]);

  // YouTube player control
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (musicService === 'youtube' && isYoutubeReady && youtubeUrl) {
      const extractYoutubeInfo = (url: string) => {
        // Extraer video ID - soporta youtube.com, music.youtube.com, y youtu.be
        const videoMatch = url.match(/(?:(?:www\.|music\.)?youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        const videoId = videoMatch ? videoMatch[1] : null;
        
        // Extraer tiempo de inicio (soporta formatos: t=55s, t=55, t=1m30s)
        let startTime = 0;
        const timeMatch = url.match(/[?&]t=([^&]+)/);
        if (timeMatch) {
          const timeStr = timeMatch[1];
          // Si termina en 's', es segundos
          if (timeStr.includes('m')) {
            // Formato minutos y segundos (1m30s)
            const parts = timeStr.match(/(\d+)m(\d+)?s?/);
            if (parts) {
              startTime = parseInt(parts[1]) * 60 + (parts[2] ? parseInt(parts[2]) : 0);
            }
          } else {
            // Solo segundos (55s o 55)
            startTime = parseInt(timeStr.replace('s', ''));
          }
        }
        
        return { videoId, startTime };
      };
      
      const { videoId, startTime } = extractYoutubeInfo(youtubeUrl);
      if (videoId && window.YT) {
        if (youtubePlayerRef.current) {
          youtubePlayerRef.current.destroy();
        }
        youtubePlayerRef.current = new window.YT.Player('youtube-player-persistent', {
          height: '0',
          width: '0',
          videoId: videoId,
          playerVars: {
            autoplay: 0, // Don't auto-play - wait for user action
            loop: 0, // NO loop - play once then silence
            playlist: videoId,
            start: startTime,
          },
          events: {
            onReady: (event: any) => {
              event.target.setVolume(musicVolume * 100);
              console.log('🎵 YouTube player ready - NOT auto-playing, waiting for chapter playback');
              console.log('🎵 Player state on ready:', event.target.getPlayerState());

              // IMPORTANT: Check if current chapter has local MP3 before auto-playing
              const currentChapter = audiobook.chapters[currentChapterIndex];
              const hasLocalMP3 = CHAPTER_BACKGROUND_MUSIC[currentChapter?.id || ''];

              if (hasLocalMP3) {
                console.log('🛑 YouTube auto-play BLOCKED - chapter uses local MP3');
                return; // Don't auto-play YouTube for chapters with local MP3
              }

              // If isPlaylistPlaying is already true (from auto-start), start playing now
              if (isPlaylistPlaying) {
                console.log('🎵 isPlaylistPlaying is true, starting playback now');
                setTimeout(() => {
                  event.target.playVideo();
                }, 100);
              }
            },
            onStateChange: (event: any) => {
              console.log('🎵 YouTube player state changed:', event.data);
              // Only sync state if the change came from user interaction, not our programmatic control
              // We'll rely on the UI state being the source of truth
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, youtubeUrl, isYoutubeReady, musicService]); // musicVolume se maneja por separado

  // Volume control for YouTube - independiente
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (youtubePlayerRef.current && youtubePlayerRef.current.setVolume && typeof youtubePlayerRef.current.setVolume === 'function') {
      try {
        youtubePlayerRef.current.setVolume(musicVolume * 100);
      } catch (error) {
        console.log('Error setting YouTube volume:', error);
      }
    }
  }, [currentView, musicVolume]);

  // Removed YouTube auto-start when chapters play to keep independence

  // ENHANCED YouTube player control - DISABLED for chapters with local MP3 (unless manual YouTube loaded)
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    const currentChapter = audiobook.chapters[currentChapterIndex];
    const hasLocalMP3 = CHAPTER_BACKGROUND_MUSIC[currentChapter?.id || ''];

    console.log('🎵 CONTROL TRIGGERED - isPlaylistPlaying:', isPlaylistPlaying);
    console.log('🎵 musicService:', musicService);
    console.log('🎵 hasPlayer:', !!youtubePlayerRef.current);
    console.log('🎵 hasUrl:', !!youtubeUrl);
    console.log('🎵 hasLocalMP3:', !!hasLocalMP3);
    console.log('🎵 hasManualYoutubeUrl:', hasManualYoutubeUrl);

    // IMPORTANT: Skip YouTube control for chapters with local MP3 (chapters 1-8)
    // UNLESS user has manually loaded a YouTube URL
    if (hasLocalMP3 && !hasManualYoutubeUrl) {
      console.log('🛑 YouTube control DISABLED - chapter uses local MP3');
      return;
    }

    if (hasManualYoutubeUrl) {
      console.log('✅ Manual YouTube loaded - ignoring local MP3');
    }

    // Must be YouTube service with player and URL
    if (musicService !== 'youtube' || !youtubePlayerRef.current || !youtubeUrl) {
      console.log('🚫 Skipping control - conditions not met');
      return;
    }

    const player = youtubePlayerRef.current;

    // Comprehensive player validation
    if (!player || typeof player.getPlayerState !== 'function' ||
        typeof player.playVideo !== 'function' || typeof player.pauseVideo !== 'function') {
      console.log('❌ Player not ready or missing methods');
      return;
    }

    try {
      const initialState = player.getPlayerState();
      console.log('🎵 INITIAL player state:', initialState);
      console.log('🎵 State legend: -1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering, 5=cued');

      // SPECIAL CASE: If manual YouTube URL is loaded, ALWAYS keep it playing
      // This overrides the isPlaylistPlaying state to prevent audiobook from pausing background music
      if (hasManualYoutubeUrl && initialState !== 1 && initialState !== 3) {
        console.log('🎵 MANUAL YOUTUBE - Auto-resuming (current state:', initialState, ')');
        player.playVideo();
        return;
      }

      if (isPlaylistPlaying || hasManualYoutubeUrl) {
        // ===== PLAY LOGIC =====
        console.log('▶️ PLAY REQUESTED');

        // Handle different player states before playing
        if (initialState === 0) { // ended
          console.log('🔄 Video ended, restarting...');
        } else if (initialState === 2) { // paused
          console.log('▶️ Video paused, resuming...');
        } else if (initialState === 1) { // already playing
          console.log('▶️ Video already playing');
          return; // No need to do anything
        }

        player.playVideo();

        // Verify play worked
        setTimeout(() => {
          const playState = player.getPlayerState();
          console.log('✅ Play verification - state:', playState);
          if (playState !== 1 && playState !== 3) { // Not playing or buffering
            console.log('⚠️ Play might have failed, trying again...');
            player.playVideo();
          }
        }, 600);

      } else {
        // ===== PAUSE LOGIC =====
        // NEVER pause if manual YouTube URL is loaded
        if (hasManualYoutubeUrl) {
          console.log('🎵 MANUAL YOUTUBE - Ignoring pause request');
          return;
        }

        console.log('⏸️ PAUSE REQUESTED');

        // Check current state before attempting pause
        if (initialState !== 1) { // Not playing
          console.log('ℹ️ Video not playing (state:', initialState, '), no need to pause');
          return;
        }

        console.log('⏸️ Video is playing, executing pause...');

        // AGGRESSIVE pause sequence for all subsequent pauses
        let pauseAttempts = 0;
        const maxPauseAttempts = 4;

        const executePause = () => {
          pauseAttempts++;
          console.log(`⏸️ Pause attempt ${pauseAttempts}/${maxPauseAttempts}`);

          // Try different approaches based on attempt number
          if (pauseAttempts === 1) {
            player.pauseVideo();
          } else if (pauseAttempts === 2) {
            // Force pause again
            player.pauseVideo();
          } else if (pauseAttempts === 3) {
            // Try stop as alternative
            if (player.stopVideo) {
              console.log('🛑 Trying stopVideo()');
              player.stopVideo();
            } else {
              player.pauseVideo();
            }
          } else if (pauseAttempts === 4) {
            // Nuclear option: mute
            if (player.mute) {
              console.log('🔇 Last resort: muting');
              player.mute();
            }
          }
          
          // Check result after each attempt
          setTimeout(() => {
            const currentState = player.getPlayerState();
            console.log(`🔍 After attempt ${pauseAttempts} - state:`, currentState);
            
            if (currentState === 1 && pauseAttempts < maxPauseAttempts) {
              // Still playing, try next method
              console.log(`⚠️ Still playing, trying method ${pauseAttempts + 1}...`);
              executePause();
            } else if (currentState !== 1) {
              console.log('✅ Pause successful! Final state:', currentState);
            } else {
              console.log('❌ All pause attempts exhausted. Final state:', currentState);
            }
          }, 400);
        };
        
        executePause();
      }
      
    } catch (error) {
      console.log('❌ YouTube control error:', error);
      console.log('Error details:', error instanceof Error ? error.message : 'Unknown error');
    }
  }, [currentView, isPlaylistPlaying, musicService, youtubeUrl, hasManualYoutubeUrl, currentChapterIndex]); // Re-run when relevant context changes

  // AGGRESSIVE YouTube monitor - keeps manual YouTube always playing
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (!hasManualYoutubeUrl || musicService !== 'youtube' || !youtubePlayerRef.current || !youtubeUrl) {
      return;
    }

    console.log('🔄 Starting aggressive YouTube monitor for manual URL');

    const monitorInterval = setInterval(() => {
      const player = youtubePlayerRef.current;
      if (!player || typeof player.getPlayerState !== 'function') {
        return;
      }

      try {
        const state = player.getPlayerState();
        // If paused (2) or ended (0), force play
        if (state === 2 || state === 0) {
          console.log('🎵 MONITOR: YouTube paused/ended (state:', state, '), forcing play...');
          player.playVideo();
        } else if (state === 1 || state === 3) {
          // Playing or buffering - all good
          // console.log('✅ MONITOR: YouTube playing (state:', state, ')');
        }
      } catch (error) {
        console.log('❌ Monitor error:', error);
      }
    }, 500); // Check every 500ms

    return () => {
      console.log('🛑 Stopping YouTube monitor');
      clearInterval(monitorInterval);
    };
  }, [currentView, hasManualYoutubeUrl, musicService, youtubeUrl]);

  // Spotify control
  const handleSpotifyLoad = (uri: string) => {
    const extractSpotifyId = (uri: string) => {
      const match = uri.match(/spotify:track:([a-zA-Z0-9]+)|open\.spotify\.com\/track\/([a-zA-Z0-9]+)/);
      return match ? (match[1] || match[2]) : null;
    };
    
    const trackId = extractSpotifyId(uri);
    if (trackId) {
      const iframe = document.getElementById('spotify-iframe-persistent') as HTMLIFrameElement;
      if (iframe) {
        // Añadir volumen inicial a la URL
        const volume = Math.round(musicVolume * 100);
        iframe.src = `https://open.spotify.com/embed/track/${trackId}?utm_source=generator&autoplay=1&volume=${volume}`;
      }
    }
  };

  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (musicService === 'spotify' && spotifyUri) {
      handleSpotifyLoad(spotifyUri);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, musicService, spotifyUri]);

  // Control de volumen de Spotify mediante postMessage
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (musicService === 'spotify') {
      const iframe = document.getElementById('spotify-iframe-persistent') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage({
            command: 'volume',
            value: musicVolume
          }, 'https://open.spotify.com');
        } catch (error) {
          // Spotify no permite control de volumen externo por seguridad
          console.log('Spotify volume control not available');
        }
      }
    }
  }, [currentView, musicVolume, musicService]);

  // Persistent YouTube playlist player
  const [persistentYoutubePlayer, setPersistentYoutubePlayer] = useState<any>(null);
  
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (currentPlaylist?.type === 'youtube' && isYoutubeReady) {
      // Initialize player for YouTube playlists (both playlist URLs and individual tracks)
      if (currentPlaylist.youtubePlaylistUrl) {
        // Handle full playlist URLs
        const extractPlaylistId = (url: string) => {
          const match = url.match(/[?&]list=([^&]+)/);
          return match ? match[1] : null;
        };
        
        const playlistId = extractPlaylistId(currentPlaylist.youtubePlaylistUrl);
        if (playlistId) {
        if (persistentYoutubePlayer) {
          persistentYoutubePlayer.destroy();
        }
        
        const newPlayer = new window.YT.Player('youtube-playlist-player-hidden', {
          height: '0',
          width: '0',
          playerVars: {
            listType: 'playlist',
            list: playlistId,
            autoplay: isPlaylistPlaying ? 1 : 0,
            index: currentPlaylistTrack || 0,
          },
          events: {
            onReady: (event: any) => {
              event.target.setVolume(musicVolume * 100);
              console.log('YouTube playlist ready, playing:', isPlaylistPlaying);
              if (isPlaylistPlaying) {
                event.target.playVideo();
              }
            },
          },
        });
        
        setPersistentYoutubePlayer(newPlayer);
      }
    } else if (currentPlaylist.tracks && currentPlaylist.tracks.length > 0) {
      // Handle playlists with individual YouTube tracks
      console.log('🎬 Initializing YouTube player for individual tracks playlist');
      if (persistentYoutubePlayer) {
        persistentYoutubePlayer.destroy();
      }
      
      // Get the first track to initialize the player
      const firstTrack = currentPlaylist.tracks[currentPlaylistTrack];
      const videoMatch = firstTrack.url.match(/(?:(?:www\.|music\.)?youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      const videoId = videoMatch ? videoMatch[1] : null;
      
      if (videoId) {
        const newPlayer = new window.YT.Player('youtube-playlist-player', {
          width: '1',
          height: '1',
          videoId: videoId,
          playerVars: {
            autoplay: isPlaylistPlaying ? 1 : 0,
            controls: 0,
            showinfo: 0,
            rel: 0,
            iv_load_policy: 3,
            modestbranding: 1,
          },
          events: {
            onReady: () => {
              console.log('🎬 Individual tracks YouTube player ready');
              if (isPlaylistPlaying) {
                newPlayer.playVideo();
              }
              newPlayer.setVolume(musicVolume * 100);
            },
            onStateChange: (event: any) => {
              if (event.data === window.YT.PlayerState.ENDED) {
                console.log('🎬 YouTube track ended, going to next');
                handleNextTrack();
              }
            },
          },
        });
        
        setPersistentYoutubePlayer(newPlayer);
      }
    }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, currentPlaylist?.id, currentPlaylist?.youtubePlaylistUrl, isYoutubeReady]);

  // Control persistent YouTube volume
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (persistentYoutubePlayer && persistentYoutubePlayer.setVolume && typeof persistentYoutubePlayer.setVolume === 'function') {
      try {
        persistentYoutubePlayer.setVolume(musicVolume * 100);
      } catch (error) {
        console.log('Error setting persistent YouTube volume:', error);
      }
    }
  }, [currentView, musicVolume, persistentYoutubePlayer]);

  // Control persistent YouTube play/pause
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (!persistentYoutubePlayer || currentPlaylist?.type !== 'youtube') {
      return;
    }

    if (musicService !== 'youtube') {
      try {
        persistentYoutubePlayer.pauseVideo?.();
      } catch (error) {
        console.log('Error pausing YouTube player when inactive:', error);
      }
      return;
    }

    try {
      if (isPlaylistPlaying && persistentYoutubePlayer.playVideo) {
        persistentYoutubePlayer.playVideo();
      } else if (!isPlaylistPlaying && persistentYoutubePlayer.pauseVideo) {
        persistentYoutubePlayer.pauseVideo();
      }
    } catch (error) {
      console.log('Error controlling persistent YouTube playback:', error);
    }
  }, [currentView, isPlaylistPlaying, currentPlaylist?.type, persistentYoutubePlayer, musicService]);

  // Persistent local audio playlist with background playback support
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    const audioElement = document.getElementById('persistent-audio-player') as HTMLAudioElement | null;
    if (!audioElement || currentPlaylist?.type === 'youtube') {
      return;
    }

    if (musicService !== 'playlist') {
      audioElement.pause();
      return;
    }

    // Configure for background playback on mobile
    audioElement.setAttribute('playsinline', 'true');
    audioElement.setAttribute('webkit-playsinline', 'true');
    audioElement.setAttribute('x-webkit-airplay', 'allow');
    
    // Add minimal event listeners for background playback
    const handlePlay = () => console.log('🎵 Background music play event');
    const handlePause = () => console.log('⏸️ Background music pause event');
    
    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);

    try {
      const { setGainForElement } = require('./utils/audioGraph');
      setGainForElement(audioElement, musicVolume);
    } catch {
      audioElement.volume = musicVolume;
    }

    if (isPlaylistPlaying) {
      // Keep playing even when screen locks
      audioElement.play().then(() => {
        console.log('🎵 Background music playing (screen lock safe)');
      }).catch(console.error);
    } else {
      audioElement.pause();
    }
    
    // Cleanup listeners
    return () => {
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
    };
  }, [currentView, isPlaylistPlaying, musicVolume, currentPlaylist?.type, musicService]);



  // Handle track changes for local playlists
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    console.log('🎧 Track change effect triggered for local playlists');
    console.log('currentPlaylistTrack:', currentPlaylistTrack);
    console.log('currentPlaylist?.type:', currentPlaylist?.type);
    console.log('isPlaylistPlaying:', isPlaylistPlaying);

    if (musicService !== 'playlist') {
      console.log('⏸️ Skipping track change - playlist service not active');
      return;
    }

    const audioElement = document.getElementById('persistent-audio-player') as HTMLAudioElement;
    
    if (!audioElement) {
      console.log('❌ No audio element found');
      return;
    }
    
    if (currentPlaylist?.type !== 'youtube' && currentPlaylist?.tracks[currentPlaylistTrack]) {
      const track = currentPlaylist.tracks[currentPlaylistTrack];
      console.log('🎵 Changing to track:', track.url);
      console.log('🔗 Track URL:', track.url);
      
      audioElement.src = track.url;
      if (isPlaylistPlaying) {
        console.log('▶️  Auto-playing new track');
        audioElement.play().catch(console.error);
      }
    } else {
      console.log('⏭️  Skipping track change (YouTube or no track)');
    }
  }, [currentView, currentPlaylistTrack, currentPlaylist, isPlaylistPlaying, musicService]);

  // Handle track changes for YouTube playlists
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (musicService !== 'youtube') {
      console.log('🚫 Skipping YouTube track change effect - service not youtube');
      return;
    }
    console.log('🎬 YouTube track change effect:', {
      playlistType: currentPlaylist?.type,
      hasYoutubePlayer: !!persistentYoutubePlayer,
      currentTrack: currentPlaylistTrack,
      hasTrack: !!currentPlaylist?.tracks[currentPlaylistTrack]
    });
    
    if (currentPlaylist?.type === 'youtube' && persistentYoutubePlayer && currentPlaylist.tracks[currentPlaylistTrack]) {
      console.log('🎬 YouTube playlist track change:', {
        currentTrack: currentPlaylistTrack,
        trackUrl: currentPlaylist.tracks[currentPlaylistTrack].url
      });
      
      const track = currentPlaylist.tracks[currentPlaylistTrack];
      const videoMatch = track.url.match(/(?:(?:www\.|music\.)?youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      const videoId = videoMatch ? videoMatch[1] : null;
      
      if (videoId) {
        console.log('🎬 Loading YouTube video:', videoId);
        if (persistentYoutubePlayer && persistentYoutubePlayer.loadVideoById) {
          try {
            console.log('🎬 Calling loadVideoById...');
            persistentYoutubePlayer.loadVideoById(videoId);
            if (isPlaylistPlaying) {
              console.log('🎬 Auto-playing after track change...');
              setTimeout(() => {
                persistentYoutubePlayer.playVideo();
              }, 100);
            }
          } catch (error) {
            console.error('❌ Error loading YouTube track:', error);
          }
        } else {
          console.log('❌ YouTube player not ready or loadVideoById not available');
        }
      } else {
        console.log('❌ No video ID extracted from URL');
      }
    }
  }, [currentView, currentPlaylistTrack, currentPlaylist, persistentYoutubePlayer, isPlaylistPlaying, musicService]);

  // Initialize persistent player when playlist is first selected
  useEffect(() => {
    // Only run when in audiobook view
    if (currentView !== 'audiobook') return;

    if (currentPlaylist) {
      console.log('Playlist loaded/changed:', {
        name: currentPlaylist.name,
        type: currentPlaylist.type,
        isPlaying: isPlaylistPlaying,
        currentTrack: currentPlaylistTrack,
        musicService: musicService,
        volume: musicVolume
      });

      // Auto-start playback if playlist was playing when saved
      if (isPlaylistPlaying && musicService === 'playlist') {
        console.log('Auto-resuming playlist playback');
      }
    }
  }, [currentView, currentPlaylist, isPlaylistPlaying, currentPlaylistTrack, musicService, musicVolume]);

  // Single return with persistent audio players + conditional views
  return (
    <>
      {/* Persistent audio players - always mounted regardless of view */}
      <div style={{ display: 'none' }}>
        {/* Background music for chapters 1-8 - Works with screen off! */}
        <audio
          id="chapter-background-music-player"
          ref={backgroundMusicRef}
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          crossOrigin="anonymous"
          preload="metadata"
          loop={true}
          onLoadedData={() => {
            try {
              console.log('🎶 Chapter background loaded:', backgroundMusicRef.current?.src);
            } catch {}
          }}
          onPlay={() => {
            console.log('🎵 Background music started');
            setIsBackgroundMusicPlaying(true);
          }}
          onPause={() => {
            console.log('⏸️ Background music paused');
            setIsBackgroundMusicPlaying(false);
          }}
          onError={(e) => console.error('❌ Background music error:', e)}
        />

        <iframe
          id="spotify-iframe-persistent"
          title="Spotify Player"
          width="0"
          height="0"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
        <div id="youtube-player-persistent"></div>

        {/* Persistent playlist player - always active regardless of tab */}
        {currentPlaylist && (
          <div id="persistent-playlist-player">
            {currentPlaylist.type === 'youtube' && (
              <>
                <div id="youtube-playlist-player-hidden"></div>
                <div id="youtube-playlist-player"></div>
              </>
            )}
            {currentPlaylist.type !== 'youtube' && currentPlaylist.tracks[currentPlaylistTrack]?.url && (
              <audio
                id="persistent-audio-player"
                src={currentPlaylist.tracks[currentPlaylistTrack].url}
                controls={false}
                style={{ display: 'none' }}
                playsInline
                webkit-playsinline="true"
                x-webkit-airplay="allow"
                crossOrigin="anonymous"
                preload="metadata"
                loop={isRepeating}
                onEnded={() => {
                  console.log('🎵 Background track ended, handling next track');
                  if (!isRepeating) {
                    handleNextTrack();
                  } else {
                    console.log('🔁 Repeating background track');
                    const audioElement = document.getElementById('persistent-audio-player') as HTMLAudioElement;
                    if (audioElement) {
                      audioElement.currentTime = 0;
                      audioElement.play().catch(console.error);
                    }
                  }
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* 3D Landing view */}
      {currentView === 'landing' && (
        <Suspense fallback={
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff'
          }}>
            <LoadingSpinner />
          </div>
        }>
          <Landing3D onBookClick={handleBookClick} />
        </Suspense>
      )}

      {/* PDF view */}
      {currentView === 'pdf' && (
        <Suspense fallback={<LoadingSpinner />}>
          <PDFViewerComponent
            pdfUrl="/pdf/_Manifestatio.pdf"
            onBack={() => setCurrentView('landing')}
          />
        </Suspense>
      )}

      {/* Audiobook view */}
      {currentView === 'audiobook' && (
        <div className="app">
      {/* Back button to return to landing */}
      <button
        onClick={() => setCurrentView('landing')}
        style={{
          position: 'fixed',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '8px',
          color: '#fff',
          cursor: 'pointer',
          fontSize: '13px',
          backdropFilter: 'blur(8px)'
        }}
      >
        <ArrowLeft size={16} />
        Volver
      </button>
      <header className="app-header">
        <div className="header-content">
          <div className="header-controls-left">
            <select
              value={audioLanguage}
              onChange={(e) => {
                const newLang = e.target.value as AudioLanguage;
                const oldLang = audioLanguage;
                setAudioLanguage(newLang);
                // Change UI language based on audio language
                if (newLang === 'es') {
                  setLanguage('es');
                } else {
                  setLanguage('en');
                }

                // Track language change
                trackAudiobookEvent.languageChange(newLang, oldLang);
              }}
              className="unified-language-select"
              title="Idioma del audio e interfaz"
            >
              {audiobook.availableLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang === 'es' && '🇪🇸'}
                  {lang === 'en' && '🇺🇸'}
                  {lang === 'fr' && '🇫🇷'}
                  {lang === 'it' && '🇮🇹'}
                  {lang === 'de' && '🇩🇪'}
                  {lang === 'pt' && '🇵🇹'}
                </option>
              ))}
            </select>
          </div>
          
          <div className="header-controls-right">
            <button
              className="share-whatsapp-btn"
              onClick={handleShareWhatsApp}
              title="Compartir por WhatsApp"
            >
              <Send size={19} />
            </button>
          </div>
          
          <div className="header-image-container">
            <img
              src="/carismatico.png"
              alt="Carismático"
              className="header-profile-image"
            />
          </div>

          <div className="header-title-stack">
            <h1 className="main-title">{audiobook.title}</h1>
            <h2 className="author-title">{audiobook.author}</h2>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="player-section">
          <AudioPlayer
            chapter={getChapterWithLanguage(currentChapterIndex)}
            nextChapter={getNextChapterWithLanguage()}
            onNextChapter={handleNextChapter}
            onPreviousChapter={handlePreviousChapter}
            bookmarkToLoad={bookmarkToLoad}
            onTimeUpdate={setCurrentPlayTime}
            onBookmarkLoaded={() => setBookmarkToLoad(null)}
            playbackRate={playbackRate}
            onPlaybackRateChange={setPlaybackRate}
            shouldAutoPlay={shouldAutoPlayNextChapter}
            onAutoPlayHandled={() => setShouldAutoPlayNextChapter(false)}
          />
        </div>

        <div className="accordion-sections">
          {/* Music Section */}
          <div className="accordion-section">
            <button 
              className="accordion-header"
              onClick={() => toggleSection('music')}
            >
              <div className="accordion-header-content">
                <Headphones size={20} />
                <span>{t.backgroundMusic}</span>
              </div>
              <ChevronDown 
                size={20} 
                className={`accordion-chevron ${!collapsedSections.music ? 'expanded' : ''}`}
              />
            </button>
            {!collapsedSections.music && (
              <div className="accordion-content">
                <Suspense fallback={<LoadingSpinner />}>
                  <MusicPlayer
                    service={musicService}
                    onServiceChange={handleMusicServiceChange}
                    musicVolume={musicVolume}
                    onVolumeChange={setMusicVolume}
                    spotifyUri={spotifyUri}
                    onSpotifyUriChange={setSpotifyUri}
                    youtubeUrl={youtubeUrl}
                    onYoutubeUrlChange={setYoutubeUrl}
                    onManualYoutubeLoad={() => {
                      setHasManualYoutubeUrl(true);
                      setIsPlaylistPlaying(true); // Auto-start playback when manually loading YouTube
                    }}
                    isPlaying={musicService === 'chapter' ? isBackgroundMusicPlaying : isPlaylistPlaying}
                    onPlayPause={() => {
                      console.log('🎵 Background music play/pause button clicked');
                      console.log('🎵 Current state - musicService:', musicService, 'isPlaylistPlaying:', isPlaylistPlaying);

                      if (musicService === 'chapter') {
                        if (backgroundMusicRef.current) {
                          const src = backgroundMusicRef.current.src;
                          if (!src) {
                            alert('Este capítulo no tiene música de fondo disponible.');
                          } else if (backgroundMusicRef.current.paused) {
                            backgroundMusicRef.current.play().catch((error) => {
                              console.error('❌ Error playing chapter background:', error);
                            });
                            setIsBackgroundMusicPlaying(true);
                          } else {
                            backgroundMusicRef.current.pause();
                            setIsBackgroundMusicPlaying(false);
                          }
                        }
                      } else if (musicService === 'playlist' && currentPlaylist) {
                        console.log('🎵 Controlling playlist playback');
                        setIsPlaylistPlaying(!isPlaylistPlaying);
                      } else if (musicService === 'youtube' && youtubeUrl) {
                        console.log('🎵 Controlling YouTube playback - Player ready:', !!youtubePlayerRef.current);
                        // Just update the state - the sync useEffect will handle the actual play/pause
                        setIsPlaylistPlaying(!isPlaylistPlaying);
                        console.log('🎵 YouTube state updated, new state:', !isPlaylistPlaying)
                      } else if (musicService === 'spotify' && spotifyUri) {
                        console.log('🎵 Controlling Spotify playback');
                        setIsPlaylistPlaying(!isPlaylistPlaying);
                      } else {
                        console.log('❌ No active music service detected');
                        alert('Primero carga una pista, playlist o URL');
                      }
                    }}
                    hasActiveMusic={true}
                    playlistManager={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PlaylistManager
                          playlists={playlists}
                          currentPlaylist={currentPlaylist}
                          currentTrackIndex={currentPlaylistTrack}
                          isPlaying={isPlaylistPlaying}
                          musicVolume={musicVolume}
                          isShuffled={isShuffled}
                          isRepeating={isRepeating}
                          onPlaylistsChange={setPlaylists}
                          onSelectPlaylist={setCurrentPlaylist}
                          onTrackChange={setCurrentPlaylistTrack}
                          onPlayPause={() => setIsPlaylistPlaying(!isPlaylistPlaying)}
                          onNextTrack={handleNextTrack}
                          onPreviousTrack={handlePreviousTrack}
                          onToggleShuffle={() => setIsShuffled(!isShuffled)}
                          onToggleRepeat={() => setIsRepeating(!isRepeating)}
                        />
                      </Suspense>
                    }
                  />
                </Suspense>
              </div>
            )}
          </div>

          {/* Chapters Section */}
          <div className="accordion-section">
            <button
              className="accordion-header"
              onClick={() => toggleSection('chapters')}
            >
              <div className="accordion-header-content">
                <Book size={20} />
                <span>{t.chapters}</span>
              </div>
              <ChevronDown
                size={20}
                className={`accordion-chevron ${!collapsedSections.chapters ? 'expanded' : ''}`}
              />
            </button>
            {!collapsedSections.chapters && (
              <div className="accordion-content">
                <Suspense fallback={<LoadingSpinner />}>
                  <ChapterList
                    chapters={audiobook.chapters}
                    currentChapterIndex={currentChapterIndex}
                    completedChapters={completedChapters}
                    onChapterSelect={handleChapterSelect}
                    currentTime={currentPlayTime}
                    onSaveBookmark={handleSaveBookmark}
                  />
                </Suspense>
              </div>
            )}
          </div>

          {/* Bookmarks Section */}
          <div className="accordion-section">
            <button 
              className="accordion-header"
              onClick={() => toggleSection('bookmarks')}
            >
              <div className="accordion-header-content">
                <BookmarkIcon size={20} />
                <span>{t.bookmarks}</span>
              </div>
              <ChevronDown 
                size={20} 
                className={`accordion-chevron ${!collapsedSections.bookmarks ? 'expanded' : ''}`}
              />
            </button>
            {!collapsedSections.bookmarks && (
              <div className="accordion-content">
                <Suspense fallback={<LoadingSpinner />}>
                  <BookmarkList
                    bookmarks={bookmarks}
                    onLoadBookmark={handleLoadBookmark}
                    onDeleteBookmark={handleDeleteBookmark}
                  />
                </Suspense>
              </div>
            )}
          </div>

          {/* Settings Section */}
          <div className="accordion-section">
            <button 
              className="accordion-header"
              onClick={() => toggleSection('settings')}
            >
              <div className="accordion-header-content">
                <Settings size={20} />
                <span>{t.settings}</span>
              </div>
              <ChevronDown 
                size={20} 
                className={`accordion-chevron ${!collapsedSections.settings ? 'expanded' : ''}`}
              />
            </button>
            {!collapsedSections.settings && (
              <div className="accordion-content">
                <div className="settings-panel">
                  <h2>{t.configuration}</h2>
                  <div className="settings-info">
                    <h3>{t.howToUse}</h3>
                    <ol>
                      <li>{t.howToUseSteps.step1}</li>
                      <li>{t.howToUseSteps.step2}</li>
                      <li>{t.howToUseSteps.step3}</li>
                    </ol>
                    
                    <h3>{t.progress}</h3>
                    <p>{t.progressInfo}</p>
                    <p>{t.completedChapters} {completedChapters.size} / {audiobook.chapters.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-links">
            <button 
              onClick={handlePlayMusicNote}
              className="footer-link music-note"
              title="Ilusión - Sergei Grosny (Música original)"
            >
              <Music size={18} />
            </button>
            
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsPDFOpen(true);
              }}
              className="footer-link book-icon"
              title="Libro"
            >
              <BookOpen size={18} />
            </button>
            
            <a
              href="https://instagram.com/dante_zetta"
              className="footer-link instagram"
              title="Instagram @dante_zetta"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram size={18} />
            </a>
          </div>
          
          <div className="footer-copyright">
            © 2025 Mistycal Works
          </div>
        </div>
      </footer>
      
      <audio 
        ref={musicAudioRef}
        preload="metadata"
        src="/audio/Ilusion_%20Sergei%20Grosny_By%20Dante.mp3"
        style={{ display: 'none' }}
        onLoadStart={() => console.log('🎵 Music loading started...')}
        onLoadedData={() => console.log('🎵 Music loaded successfully')}
        onError={(e) => console.error('❌ Music loading error:', e)}
      />
      
        <PDFReader
          isOpen={isPDFOpen}
          onClose={() => setIsPDFOpen(false)}
        />
      </div>
      )}
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
